import React, { Component } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import TableStateCard from '../components/TableStateCard';
import { validateSession } from '../utils/functions';
import { navigateTo } from '../utils/navigation';
import {
    getCollection,
    updateCollection,
    deleteCollection,
    normalizeCollection,
    normalizeLabeledProduct,
    collectionCounts,
    PLACEMENTS,
    PLACEMENT_NONE,
    placementLabel,
    placementHelp,
    isKnownPlacement,
    isPlaced,
    isSystemSlug,
    isNotFound,
    isConflict,
} from '../utils/collectionsApi';

// Configuración de una colección de la home: nombre, dónde se muestra, en qué orden, con qué arte
// y —lo importante— si se publica o no.
//
// Permisos (decisión del dueño): admin y manager pueden TODO, incluido publicar. Las dos
// salvaguardas no son de permisos sino de intención:
//   · toda colección nace privada (is_public = 0), así que publicar es siempre un acto explícito;
//   · publicar/despublicar no viaja escondido en el "Guardar" del formulario, tiene su propio
//     botón con confirmación que dice qué va a pasar y con cuántos productos.
// Enterrar el switch adentro del form haría que un click de más publicara la home sin que nadie
// lo hubiera decidido, y el "deshacer" de eso ya lo vieron los clientes.
class EditLabelPage extends Component {

    constructor() {
        super();
        const cachedRole = Number(localStorage.getItem('role') || 0);
        this.state = {
            checked: false,
            role: cachedRole,

            labelId: null,
            collection: null,
            counts: { asignados: null, visibles: null },
            notFound: false,
            loadError: null,
            loading: true,

            form: { name: '', placement: PLACEMENT_NONE, sortOrder: 0, imagePath: '' },
            formError: null,
            saving: false,
            savedAt: null,

            publishing: false,
            deleting: false,
        };
        this.mounted = false;
    }

    async componentDidMount() {
        this.mounted = true;
        // Configurar una colección no toca la caja: sin el flag, un manager con la caja cerrada
        // termina en /caja al abrir esta pantalla.
        const role = await validateSession(true);
        if (!this.mounted) return;

        const params = new URLSearchParams(this.props.location.search);
        const rawId = params.get('id');
        const labelId = Number(rawId);

        if (!rawId || !Number.isFinite(labelId) || labelId <= 0) {
            this.setState({ checked: true, role, notFound: true, loading: false });
            return;
        }

        this.setState({ checked: true, role, labelId });
        this.fetchDetail(labelId);
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    fetchDetail = (labelId = this.state.labelId) => {
        this.setState({ loading: true, loadError: null });
        return getCollection(labelId)
            .then((data) => {
                if (!this.mounted) return;
                const labelInfo = data && data.labelInfo;
                if (!labelInfo) {
                    this.setState({ notFound: true, loading: false });
                    return;
                }
                const collection = normalizeCollection(labelInfo);
                const products = (data.labeledProducts || []).map(normalizeLabeledProduct);
                this.setState({
                    collection,
                    counts: collectionCounts(collection, products),
                    notFound: false,
                    loading: false,
                    form: {
                        name: collection.name === '(sin nombre)' ? '' : collection.name,
                        placement: collection.placement,
                        sortOrder: collection.sortOrder,
                        imagePath: collection.imagePath,
                    },
                });
            })
            .catch((error) => {
                if (!this.mounted) return;
                if (isNotFound(error)) {
                    this.setState({ notFound: true, loading: false });
                    return;
                }
                this.setState({ loadError: error.message, loading: false });
            });
    }

    setField = (field, value) => {
        this.setState((prev) => ({ form: { ...prev.form, [field]: value }, formError: null, savedAt: null }));
    }

    // Campos del formulario que difieren de lo guardado. Publicar aplica sobre lo GUARDADO, así
    // que si hay cambios sueltos hay que decirlo: si no, alguien cambia la ubicación, publica sin
    // guardar y la home sale con la ubicación vieja sin que nada se lo haya avisado.
    hasUnsavedChanges = () => {
        const { form, collection } = this.state;
        if (!collection) return false;
        return String(form.name || '').trim() !== collection.name
            || form.placement !== collection.placement
            || Number(form.sortOrder) !== collection.sortOrder
            || (form.imagePath || '') !== (collection.imagePath || '');
    }

    // Una escritura que falla con "esa colección no existe" NO es un error de formulario: la
    // borraron desde otra pestaña (o desde otra máquina) mientras esta seguía abierta. Mostrarlo
    // como texto rojo abajo del campo deja al usuario apretando "Guardar" contra algo que ya no
    // está; se cambia la pantalla entera al estado de "no existe", que además ofrece la salida al
    // listado. Vale para guardar, para publicar y para eliminar: los tres escriben sobre una
    // colección que puede haber desaparecido, y sólo la carga inicial lo contemplaba.
    handleWriteError = (error, extra = {}) => {
        if (!this.mounted) return;
        if (isNotFound(error)) {
            this.setState({
                ...extra,
                notFound: true,
                formError: null,
                collection: null,
            });
            return;
        }
        this.setState({ ...extra, formError: extra.formError || error.message });
    }

    // ── Guardar (todo menos publicar) ────────────────────────────────────────────────────

    onClickSave = () => {
        const { form, collection } = this.state;
        const name = String(form.name || '').trim();

        if (!name) {
            this.setState({ formError: 'El nombre es obligatorio.' });
            return;
        }

        const sortOrder = Number(form.sortOrder);
        if (!Number.isFinite(sortOrder) || sortOrder < 0) {
            this.setState({ formError: 'El orden tiene que ser un número mayor o igual a 0.' });
            return;
        }

        // Sólo lo que cambió: un PATCH con todos los campos pisaría con datos de esta pantalla
        // algo que otra persona haya tocado mientras tanto.
        const patch = {};
        if (name !== collection.name) patch.name = name;
        if (form.placement !== collection.placement) patch.placement = form.placement;
        if (sortOrder !== collection.sortOrder) patch.sort_order = sortOrder;
        if ((form.imagePath || '') !== (collection.imagePath || '')) patch.image_path = form.imagePath || null;

        if (Object.keys(patch).length === 0) {
            this.setState({ formError: 'No hay cambios para guardar.' });
            return;
        }

        this.setState({ saving: true, formError: null });
        updateCollection(this.state.labelId, patch)
            .then(() => {
                if (!this.mounted) return;
                this.setState({ saving: false, savedAt: new Date() });
                this.fetchDetail();
            })
            .catch((error) => this.handleWriteError(error, { saving: false }));
    }

    // ── Publicar / despublicar ───────────────────────────────────────────────────────────

    onClickTogglePublish = () => {
        const collection = this.state.collection;
        const publicando = !collection.isPublic;

        if (!window.confirm(this.publishConfirmText(collection, this.state.counts, publicando))) {
            return;
        }

        this.setState({ publishing: true, formError: null });
        updateCollection(this.state.labelId, { is_public: publicando ? 1 : 0 })
            .then(() => {
                if (!this.mounted) return;
                this.setState({ publishing: false });
                this.fetchDetail();
            })
            .catch((error) => this.handleWriteError(error, { publishing: false }));
    }

    publishConfirmText(collection, counts, publicando) {
        const sinGuardar = this.hasUnsavedChanges()
            ? '\n\nOJO: tenés cambios sin guardar en el formulario. Esta acción NO los guarda.'
            : '';

        if (!publicando) {
            return `La colección "${collection.name}" va a DEJAR de verse en la home.\n\n`
                + `Los productos siguen asignados: sólo se oculta la sección.${sinGuardar}\n\n¿Confirmás?`;
        }

        const asignados = counts.asignados;
        const visibles = counts.visibles;

        const lineas = [`La colección "${collection.name}" va a EMPEZAR a verse en la home, para todos los clientes.`];
        lineas.push('');
        lineas.push(`Ubicación: ${placementLabel(collection.placement)}`);
        if (asignados != null) {
            lineas.push(`Productos asignados: ${asignados}${visibles != null ? ` (se ven en la web: ${visibles})` : ''}`);
        }
        if (!isPlaced(collection.placement)) {
            lineas.push('');
            lineas.push('OJO: no tiene ubicación asignada, así que publicarla no la muestra en ningún lado.');
        }
        // Sin curar todavía no es un problema (la tienda no dibuja la sección vacía), así que se
        // dice sin "OJO". Curada y sin nada visible sí lo es.
        if (asignados === 0) {
            lineas.push('');
            lineas.push('Todavía no tiene productos: la sección no se va a mostrar hasta que le agregues alguno.');
        } else if (visibles === 0) {
            lineas.push('');
            lineas.push('OJO: ninguno de los productos que curaste se ve en la web (están ocultos o archivados).');
        }
        if (sinGuardar) {
            lineas.push('');
            lineas.push(sinGuardar.trim());
        }
        lineas.push('');
        lineas.push('¿Confirmás?');
        return lineas.join('\n');
    }

    // ── Eliminar ─────────────────────────────────────────────────────────────────────────

    onClickDelete = () => {
        const collection = this.state.collection;
        const asignados = this.state.counts.asignados;
        const cuantos = asignados != null ? `sus ${asignados} asignaciones` : 'sus asignaciones';
        const confirmText = `Se va a eliminar la colección "${collection.name}" y ${cuantos} de productos.`
            + '\nLos productos NO se borran.\n\n¿Confirmás?';
        if (!window.confirm(confirmText)) {
            return;
        }

        this.setState({ deleting: true, formError: null });
        deleteCollection(this.state.labelId)
            .then(() => {
                if (!this.mounted) return;
                navigateTo('/labels');
            })
            .catch((error) => this.handleWriteError(error, {
                deleting: false,
                formError: isConflict(error)
                    ? `${error.message} Es una colección de sistema: la home la pide por su nombre corto, así que borrarla dejaría una sección rota. Si no la querés mostrar, despublicala.`
                    : undefined,
            }));
    }

    // ── Render ───────────────────────────────────────────────────────────────────────────

    renderPlacementOptions() {
        const options = PLACEMENTS.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
        ));

        // Un `placement` que este panel no conoce (lo agregó una versión más nueva del backend) se
        // ofrece igual como opción: si no estuviera, el select caería al primer valor y "Guardar"
        // reescribiría en silencio la ubicación de una sección de la home.
        if (!isKnownPlacement(this.state.form.placement)) {
            options.push(
                <option key={this.state.form.placement} value={this.state.form.placement}>
                    {placementLabel(this.state.form.placement)}
                </option>
            );
        }
        return options;
    }

    renderForm() {
        const { collection, form } = this.state;
        const sistema = isSystemSlug(collection.slug);

        return (
            <div className="row">
                <div className="col-lg-8">
                    <div className="card card-small mb-4">
                        <div className="card-header border-bottom">
                            <h6 className="m-0">Datos de la colección</h6>
                        </div>
                        <ul className="list-group list-group-flush">
                            <li className="list-group-item p-3">
                                <div className="form-row">
                                    <div className="form-group col-md-7">
                                        <label htmlFor="name">Nombre</label>
                                        <input
                                            id="name"
                                            type="text"
                                            className="form-control"
                                            value={form.name}
                                            maxLength={45}
                                            onChange={(e) => this.setField('name', e.target.value)}
                                        />
                                        <small className="form-text text-muted">Es el título que ve el cliente arriba de la sección.</small>
                                    </div>
                                    <div className="form-group col-md-5">
                                        <label htmlFor="slug">Nombre corto (slug)</label>
                                        <input id="slug" type="text" className="form-control" value={collection.slug || '—'} readOnly disabled />
                                        <small className="form-text text-muted">
                                            {sistema
                                                ? 'Colección de sistema: la home la pide por este nombre corto y no se puede cambiar ni borrar.'
                                                : 'Se genera al crear la colección y no se edita: la tienda la pide por acá.'}
                                        </small>
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group col-md-5">
                                        <label htmlFor="placement">Dónde se muestra en la home</label>
                                        <select
                                            id="placement"
                                            className="form-control"
                                            value={form.placement}
                                            onChange={(e) => this.setField('placement', e.target.value)}
                                        >
                                            {this.renderPlacementOptions()}
                                        </select>
                                        <small className="form-text text-muted">{placementHelp(form.placement)}</small>
                                    </div>
                                    <div className="form-group col-md-3">
                                        <label htmlFor="sortOrder">Orden</label>
                                        <input
                                            id="sortOrder"
                                            type="number"
                                            min="0"
                                            className="form-control"
                                            value={form.sortOrder}
                                            onChange={(e) => this.setField('sortOrder', e.target.value)}
                                        />
                                        <small className="form-text text-muted">Entre las colecciones del mismo lugar, el número más chico va primero.</small>
                                    </div>
                                    <div className="form-group col-md-4">
                                        <label htmlFor="imagePath">Imagen (ruta)</label>
                                        <input
                                            id="imagePath"
                                            type="text"
                                            className="form-control"
                                            value={form.imagePath}
                                            maxLength={255}
                                            placeholder="Vacío = la tienda usa su imagen por defecto"
                                            onChange={(e) => this.setField('imagePath', e.target.value)}
                                        />
                                        <small className="form-text text-muted">Arte propia del tile. Si queda vacía, la tienda usa la que ya usa hoy.</small>
                                    </div>
                                </div>

                                {form.imagePath ? (
                                    <div className="mb-3">
                                        <img
                                            src={form.imagePath}
                                            alt="Vista previa"
                                            style={{ maxHeight: '110px', border: '1px solid #e1e5eb', borderRadius: '4px' }}
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    </div>
                                ) : null}

                                {this.state.formError ? (
                                    <div className="alert alert-danger p-2" style={{ fontSize: '13px' }}>{this.state.formError}</div>
                                ) : null}
                                {this.state.savedAt ? (
                                    <div className="alert alert-success p-2" style={{ fontSize: '13px' }}>Cambios guardados.</div>
                                ) : null}
                            </li>
                        </ul>
                        <div className="card-footer border-top">
                            <button type="button" className="btn btn-white mr-2" onClick={() => navigateTo('/labels')}>
                                Volver
                            </button>
                            <button
                                type="button"
                                className="mb-0 btn btn-primary"
                                style={{ float: 'right' }}
                                disabled={this.state.saving}
                                onClick={this.onClickSave}
                            >
                                {this.state.saving ? 'Guardando…' : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="col-lg-4">
                    {this.renderPublishCard()}
                    {this.renderDangerCard(sistema)}
                </div>
            </div>
        );
    }

    renderPublishCard() {
        const collection = this.state.collection;
        const { asignados, visibles } = this.state.counts;

        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom">
                    <h6 className="m-0">Publicación</h6>
                </div>
                <div className="card-body p-3">
                    <p className="m-0 mb-2">
                        Estado:{' '}
                        <span className={`badge ${collection.isPublic ? 'badge-success' : 'badge-secondary'}`}>
                            {collection.isPublic ? 'Publicada en la web' : 'Privada'}
                        </span>
                    </p>
                    <p className="m-0 mb-3" style={{ color: '#818ea3', fontSize: '13px' }}>
                        {collection.isPublic
                            ? 'Los clientes la están viendo en la home.'
                            : 'Todavía no se ve en la home. Podés curarla tranquilo y publicarla cuando esté lista.'}
                    </p>

                    <p className="m-0 mb-3" style={{ fontSize: '13px' }}>
                        {asignados != null ? `${asignados} producto(s) asignado(s)` : 'Cantidad de productos no informada'}
                        {visibles != null ? ` · ${visibles} se ven en la web` : ''}
                    </p>

                    {/* Sin curar todavía = estado normal (la tienda no dibuja la sección hasta
                        que tenga productos). Curada y sin nada visible = problema real. */}
                    {asignados === 0 ? (
                        <div className="alert alert-info p-2" style={{ fontSize: '13px' }}>
                            Todavía no curaste ningún producto. La sección no se muestra en la tienda hasta que
                            le agregues al menos uno.
                        </div>
                    ) : visibles === 0 ? (
                        <div className="alert alert-warning p-2" style={{ fontSize: '13px' }}>
                            Ninguno de los productos que curaste se ve en la web (están ocultos o archivados).
                        </div>
                    ) : null}
                    {!isPlaced(collection.placement) ? (
                        <div className="alert alert-warning p-2" style={{ fontSize: '13px' }}>
                            Sin ubicación asignada: aunque la publiques, no se muestra en ningún lado.
                        </div>
                    ) : null}
                    {this.hasUnsavedChanges() ? (
                        <div className="alert alert-info p-2" style={{ fontSize: '13px' }}>
                            Tenés cambios sin guardar en el formulario. Publicar aplica sobre lo guardado.
                        </div>
                    ) : null}

                    <button
                        type="button"
                        className={`btn btn-block ${collection.isPublic ? 'btn-outline-secondary' : 'btn-success'}`}
                        disabled={this.state.publishing}
                        onClick={this.onClickTogglePublish}
                    >
                        {this.state.publishing
                            ? 'Aplicando…'
                            : (collection.isPublic ? 'Sacar de la web' : 'Publicar en la web')}
                    </button>
                    <small className="form-text text-muted">Pide confirmación antes de aplicarse.</small>

                    <hr />
                    <button
                        type="button"
                        className="btn btn-white btn-block"
                        onClick={() => navigateTo(`/labelAssign?id=${this.state.labelId}`)}
                    >
                        Curar productos
                    </button>
                </div>
            </div>
        );
    }

    renderDangerCard(sistema) {
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom">
                    <h6 className="m-0">Eliminar</h6>
                </div>
                <div className="card-body p-3">
                    {sistema ? (
                        <p className="m-0" style={{ color: '#818ea3', fontSize: '13px' }}>
                            Esta es una colección de sistema: la home la pide por su nombre corto
                            (<code>{this.state.collection.slug}</code>), así que borrarla dejaría una sección rota.
                            Si no la querés mostrar, <strong>despublicala</strong>.
                        </p>
                    ) : (
                        <>
                            <p className="m-0 mb-3" style={{ color: '#818ea3', fontSize: '13px' }}>
                                Se borra la colección y sus asignaciones. Los productos no se tocan.
                            </p>
                            <button
                                type="button"
                                className="btn btn-outline-danger btn-block"
                                disabled={this.state.deleting}
                                onClick={this.onClickDelete}
                            >
                                {this.state.deleting ? 'Eliminando…' : 'Eliminar colección'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    renderBody() {
        if (this.state.notFound) {
            return (
                <div>
                    <TableStateCard
                        icon="search_off"
                        title="Esa colección no existe"
                        message="Puede que la hayan borrado, o que el link esté mal. Volvé al listado y elegila de ahí."
                    />
                    <button type="button" className="btn btn-primary mt-3" onClick={() => navigateTo('/labels')}>
                        Volver a las colecciones
                    </button>
                </div>
            );
        }

        if (this.state.loading && this.state.collection == null) {
            return <TableStateCard loading />;
        }

        if (this.state.loadError && this.state.collection == null) {
            return (
                <TableStateCard
                    icon="error_outline"
                    title="No pudimos cargar la colección"
                    message={this.state.loadError}
                />
            );
        }

        return this.renderForm();
    }

    render() {
        if (!this.state.checked) {
            return (<div></div>);
        }

        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={15} role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4">
                            <HeaderComponent title="Etiquetas" subtitle="Configurar una colección de la home" />
                            {this.renderBody()}
                        </div>
                    </main>
                </div>
            </div>
        );
    }
}

export default EditLabelPage;
